import { useState, useRef, useEffect, useCallback, TouchEvent } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  Volume2,
  VolumeX,
  Sparkles,
  MapPin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { HERO_SLIDES } from "@/components/heroSlidesData";

/**
 * HeroMobile — Versión optimizada para pantallas táctiles y ergonomía de pulgar (Thumb Zone).
 * Incluye gestos táctiles de deslizamiento (Swipe), barras tipo historias y altura dinámica (100dvh).
 */
export const HeroMobile = () => {
  const { openAuthModal } = useAuth();
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  // Soporte para gestos táctiles (Swipe)
  const touchStartX = useRef<number | null>(null);

  const currentSlide = HERO_SLIDES[activeIndex];

  const handleNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % HERO_SLIDES.length);
    setProgress(0);
  }, []);

  const handlePrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);
    setProgress(0);
  }, []);

  const goToSlide = (idx: number) => {
    setActiveIndex(idx);
    setProgress(0);
  };

  useEffect(() => {
    videoRefs.current.forEach((videoEl, idx) => {
      if (!videoEl) return;
      if (idx === activeIndex) {
        videoEl.currentTime = 0;
        const playPromise = videoEl.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {});
        }
      } else {
        videoEl.pause();
      }
    });
    setProgress(0);
  }, [activeIndex]);

  const toggleMute = () => {
    setIsMuted((prev) => {
      const next = !prev;
      videoRefs.current.forEach((v) => {
        if (v) v.muted = next;
      });
      return next;
    });
  };

  // Gestos swipe
  const onTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (diff > 50) {
      handleNext(); // Swipe hacia la izquierda -> siguiente
    } else if (diff < -50) {
      handlePrev(); // Swipe hacia la derecha -> anterior
    }
    touchStartX.current = null;
  };

  return (
    <section
      data-section="hero"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className="relative w-full h-[calc(100dvh-4.5rem)] min-h-[560px] bg-stone-950 text-white overflow-hidden flex flex-col justify-between select-none"
    >
      {/* 1. Fondo de vídeo continuo con crossfade */}
      <div className="absolute inset-0 z-0">
        {HERO_SLIDES.map((slide, index) => {
          const isActive = index === activeIndex;
          return (
            <video
              key={slide.id}
              ref={(el) => {
                videoRefs.current[index] = el;
              }}
              src={slide.videoSrc}
              poster={slide.posterSrc}
              muted={isMuted}
              playsInline
              preload={index === 0 ? "auto" : "metadata"}
              onEnded={handleNext}
              onTimeUpdate={(e) => {
                if (isActive) {
                  const v = e.currentTarget;
                  if (v.duration) {
                    setProgress((v.currentTime / v.duration) * 100);
                  }
                }
              }}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${
                isActive ? "opacity-100 z-10" : "opacity-0 pointer-events-none z-0"
              }`}
            />
          );
        })}

        {/* Gradientes diseñados para contraste móvil */}
        <div className="absolute inset-0 z-20 bg-gradient-to-b from-stone-950/80 via-stone-950/40 to-stone-950/95" />
      </div>

      {/* 2. Cabecera táctil: Barras de progreso de historias y botón de sonido */}
      <div className="relative z-30 pt-3 px-4 flex flex-col gap-3">
        {/* Barras de historias */}
        <div className="grid grid-cols-3 gap-2 w-full">
          {HERO_SLIDES.map((slide, idx) => {
            const isCurrent = idx === activeIndex;
            const isPassed = idx < activeIndex;

            return (
              <button
                key={slide.id}
                type="button"
                onClick={() => goToSlide(idx)}
                className="h-1 bg-white/25 rounded-full overflow-hidden"
              >
                <div
                  className={`h-full rounded-full transition-all ease-linear ${
                    isCurrent ? "bg-primary" : isPassed ? "bg-white/90" : "bg-transparent"
                  }`}
                  style={{
                    width: isCurrent ? `${progress}%` : isPassed ? "100%" : "0%",
                  }}
                />
              </button>
            );
          })}
        </div>

        {/* Fila superior: badge + sonido */}
        <div className="flex items-center justify-between">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-[11px] font-semibold text-stone-200">
            <MapPin className="h-3 w-3 text-primary" />
            <span>Marketplace local</span>
            <span className="w-1 h-1 rounded-full bg-stone-400" />
            <span className="text-primary font-bold">{currentSlide.tag}</span>
          </div>

          <button
            type="button"
            onClick={toggleMute}
            aria-label={isMuted ? "Activar audio" : "Silenciar audio"}
            className="flex items-center justify-center p-2 rounded-full bg-stone-900/70 border border-white/10 text-stone-200 backdrop-blur-md"
          >
            {isMuted ? (
              <VolumeX className="h-4 w-4" />
            ) : (
              <Volume2 className="h-4 w-4 text-primary animate-pulse" />
            )}
          </button>
        </div>
      </div>

      {/* 3. Área Central/Inferior: Textos dinámicos y Thumb-Zone CTAs */}
      <div className="relative z-30 px-5 pb-5 flex flex-col justify-end space-y-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35 }}
            className="space-y-2.5"
          >
            <h1 className="text-3xl font-display font-extrabold tracking-tight text-white leading-tight">
              {currentSlide.titlePart1}
              <span className="text-primary block sm:inline">
                {currentSlide.titleHighlight}
              </span>
            </h1>

            <p className="text-sm text-stone-200 leading-snug line-clamp-3 font-light">
              {currentSlide.description}
            </p>
          </motion.div>
        </AnimatePresence>

        {/* Thumb-Zone CTAs */}
        <div className="flex flex-col gap-2.5 pt-2">
          <Button
            size="lg"
            className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-13 text-base font-bold shadow-lg w-full flex items-center justify-center gap-2"
            asChild
          >
            <Link to="/catalogo">
              Explorar catálogo en tu ciudad
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>

          <Button
            size="lg"
            variant="outline"
            className="rounded-full border-white/20 bg-stone-900/70 text-white h-12 text-sm font-semibold w-full"
            onClick={() => openAuthModal("signup")}
          >
            Publicar mis sets
          </Button>
        </div>

        {/* Micro-píldoras de confianza */}
        <div className="flex items-center justify-center gap-4 pt-1 text-[11px] text-stone-300">
          <span className="inline-flex items-center gap-1">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
            Entrega 0 € o envío
          </span>
          <span className="inline-flex items-center gap-1">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
            Fianza opcional y seguro
          </span>
        </div>
      </div>
    </section>
  );
};

export default HeroMobile;
